import { FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { clsx } from 'clsx';

import cls from './input-select.module.css';
import { useState, useEffect } from 'react';
import { LightTooltip } from '@components/LightTooltip';
import { InfoCircleFilled } from '@ant-design/icons';

interface InputSelectProps {
  name: string;
  label: string;
  selectedValue: number | string | string[] | null;
  values: string[];
  options?: {value: string | number, label: string}[];
  onChange: (event: SelectChangeEvent<string | null>) => void;
  labelWidth?: number;
  error?: string;
  className?: string;
  selectClassName?: string;
  centered?: boolean;
  multiple?: boolean;
  displayEmpty?: boolean;
  tooltip?: JSX.Element | string;
  wordWrap?: boolean;
  disabled?: boolean;
}

export const InputSelect = ({
  label,
  selectedValue,
  values,
  options,
  onChange,
  name,
  labelWidth,
  error,
  className,
  selectClassName,
  centered,
  multiple,
  displayEmpty,
  tooltip,
  wordWrap = false,
  disabled,
}: InputSelectProps) => {
  const [value, setValue] = useState<any>(selectedValue || '');
  const hasOptions = Array.isArray(options);
  const optionsLength = hasOptions ? options.length : values.length;

  useEffect(() => {
    if (multiple) {
      setValue(Array.isArray(selectedValue) ? selectedValue : []);
    } else {
      setValue(selectedValue ?? '');
    }
  }, [selectedValue, multiple]);

  return (
    <div className={cls._}>
      <label
        className={cls.label}
        style={{ minWidth: labelWidth ? labelWidth : 100 }}
      >
        {tooltip ? (
          <LightTooltip extraWidth title={tooltip} placement="top" >
            <span className={cls.info}>{label} <InfoCircleFilled className={cls.icon} /></span>
          </LightTooltip>
        ) : label}
      </label>
      <FormControl
        variant="standard"
        className={clsx(cls.form, className, centered && cls.centered)}
      >
        <Select
          name={name}
          className={clsx(selectClassName, wordWrap && 'wordWrap')}
          value={optionsLength > 0 ? value : ''}
          onChange={e => {
            setValue(e.target.value);
            onChange(e);
          }}
          multiple={multiple}
          displayEmpty={displayEmpty}
          variant="standard"
          disabled={disabled}
        >
          {displayEmpty && <MenuItem value="">&nbsp;</MenuItem>}
          {hasOptions && options.map(o => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
          {!hasOptions && values.map(value => (
            <MenuItem key={value} value={value}>{value}</MenuItem>
          ))}
        </Select>
        {error && <span className={cls.error}>{error}</span>}
      </FormControl>
    </div>
  );
};
